import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { getClass } from '../../../utils/component-helpers';
import { api } from '../../../services/api';
import ModalWrapper from '../../base/modals/modal-wrapper';

import { titleCase } from '../../base/common/utils';
import UserRoleDropdown from './user-role-dropdown';
import AccountUserDropdown from './account-user-dropdown';
import Icon from '../../ui/icon';
import AccountService from './account-service';

import './styles/account-user-modal.scss';

const roleOptions = [
  { label: 'Primary', value: true },
  { label: 'Secondary', value: false },
];

const UserModal = ({
  setShowModal,
  modal,
  setUsers,
  shortName,
  accountId,
  showError,
  userData,
  accountType,
}) => {
  const fields = ['add', 'edit'].includes(modal) ? ['first_name', 'last_name', 'email'] : ['email'];
  const headerTxt =
    modal === 'add'
      ? 'Create User'
      : modal === 'link'
      ? 'Link Existing User'
      : `${titleCase(modal)} User`;
  const subtextVerb = modal === 'add' ? 'creat' : modal;
  const buttonVerb = modal === 'add' ? 'Create' : titleCase(modal);

  const [inputState, setInputState] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [id, setId] = useState('');
  const [role, setRole] = useState(roleOptions[0]);
  const [userResults, setUserResults] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (modal === 'edit') {
      setId(userData.uid);
      setRole(roleOptions.find(option => option.label === userData.role));
      setInputState({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
      });
    }
  }, []);

  function handleInputChange(value, field) {
    setInputState({
      ...inputState,
      [field]: value,
    });
  }

  function handleSubmit() {
    const newUser = { ...inputState, username: inputState.email };
    if (modal === 'add') {
      AccountService.createUser(newUser)
        .then(res => {
          const { uid } = res.data.data[0];
          addUserToAccount(uid, { ...newUser, uid });
        })
        .catch(e => {
          if (Array.isArray(e)) {
            setError(e[0] && e[0].message);
            setFieldErrors(e[1]);
          } else {
            setError(e ? e.message : 'Error');
          }
        });
    } else if (modal === 'link') {
      addUserToAccount(inputState.uid, newUser);
    } else {
      AccountService.editUser(newUser, id)
        .then(res => {
          changeRole(res.data.data[0]);
        })
        .catch(e => {
          if (Array.isArray(e)) {
            setError(e[0] && e[0].message);
            setFieldErrors(e[1]);
          } else {
            setError(e && e.message);
          }
        });
    }
  }

  function addUserToAccount(uid, newUser) {
    api
      .put(`/api/v0/users/${uid}/${accountType}s/${accountId}/`, { is_org_admin: role.value })
      .then(res => {
        setUsers(prevState => {
          const prevUsers = cloneDeep(prevState);
          return !prevUsers.map(user => user.uid).includes(newUser.uid)
            ? [...prevUsers, { ...newUser, role: role.label }]
            : prevUsers;
        });
        setShowModal(null);
        showError('User linked');
      })
      .catch(e => showError(`User linking failed. ${e && e.message}`));
  }

  function changeRole(newUser) {
    api
      .put(`/api/v0/users/${id}/${accountType}s/${accountId}/`, { is_org_admin: role.value })
      .then(res => {
        setUsers(prevState => {
          const newUsers = cloneDeep(prevState);
          const index = newUsers.findIndex(user => user.uid === newUser.uid);
          newUsers.splice(index, 1, { ...newUser, role: role.label });
          return newUsers;
        });
        setShowModal(null);
        showError('User edited');
      })
      .catch(e => showError(`User edit failed. ${e && e.message}`));
  }

  async function getUsers() {
    const userResults = await api.get('/api/v0/users/', { get_all: true });
    setUserResults(userResults.data.data);
  }

  function handleModalBodyClick(e) {
    setOpenDropdown(null);
    e.stopPropagation();
  }

  const fieldItems = fields.map((field, index) => {
    const subtext =
      field === 'email' ? (
        <div className="subtext">
          User will receive an email confirmation once their account is {subtextVerb}ed.
        </div>
      ) : null;

    const textInput = (
      <input
        type="text"
        value={inputState[field]}
        onChange={e => handleInputChange(e.target.value, field)}
      />
    );

    const input =
      field === 'email' ? (
        modal === 'link' ? (
          inputState.email ? (
            <div className="email-input">
              <div className="selected-email" onClick={() => handleInputChange('', 'email')}>
                <div className="txt">{inputState.email}</div>
                <div className="x-icon">
                  <Icon iconName="x" size={12} />
                </div>
              </div>
              {subtext}
            </div>
          ) : (
            <div className="email-input">
              <AccountUserDropdown
                results={userResults}
                inputState={inputState}
                setInputState={setInputState}
                setOpenDropdown={setOpenDropdown}
                openDropdown={openDropdown}
              />
              {subtext}
            </div>
          )
        ) : (
          <div className="email-input">
            {textInput}
            {subtext}
          </div>
        )
      ) : (
        textInput
      );

    return (
      <div
        className={getClass('form-item', field.replace(/_/g, '-'), {
          errored: fieldErrors.includes(field),
        })}
        key={index}
      >
        <label>{field.replace(/_/g, ' ')}</label>
        {input}
      </div>
    );
  });

  return (
    <div className={`account-user-modal ${modal}`}>
      <ModalWrapper closeModal={() => setShowModal(null)}>
        <div onClick={handleModalBodyClick}>
          <h1>{headerTxt}</h1>
          <h2 className="error">{error}</h2>
          <div className="form">
            <div className="form-item">
              <label>Company</label>
              <div className="short-name">{shortName}</div>
            </div>
            <div>
              {fieldItems}
              <div className="form-item">
                <label>Role</label>
                <div className="role-dropdown">
                  <UserRoleDropdown
                    items={roleOptions}
                    selectItem={index => setRole(roleOptions[index])}
                    label={role.label}
                    setOpenDropdown={setOpenDropdown}
                    openDropdown={openDropdown}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="btns">
            <button className="cancel-btn" onClick={() => setShowModal(null)}>
              Cancel
            </button>
            <button className="submit-btn" onClick={handleSubmit}>
              {buttonVerb} User
            </button>
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default UserModal;
